import { NodeDetailPage, type NodeDetailConfig } from '@/components/nodes/NodeDetailPage'
import { taskService, documentService } from '@/services'
import type { Task } from '@/types'
import { Status } from '@/types/enums'
import { NodePropertyBadge, NodeDate, type MessageNodeConfig } from '@/components/nodes'
import { HistoryCard, AssociationsCard } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'

export default function TaskDetailPage() {
  const { user: _user } = useAuth()

  // Create a service wrapper that includes document loading
  const taskServiceWithDocuments = {
    ...taskService,
    async getById(id: string): Promise<Task | null> {
      const taskData = await taskService.getByIdWithMessages(id)
      if (!taskData) return null

      // Load documents associated with this task
      const documents = await documentService.getDocumentsForTask(id)
      return { ...taskData, documents }
    },
    delete: taskService.delete.bind(taskService),
    async updateStatus(id: string, status: Status): Promise<void> {
      await taskService.updateStatus(id, status)
    },
    getBlockedTasks: taskService.getBlockedTasks.bind(taskService),
  }

  // Configuration for MessageThread to hide workspaces, projects, and tasks
  const messageConfig: MessageNodeConfig = {
    showTitle: false,
    showDescription: true,
    showReplyCount: true,
    showMessageType: true,
    showReadStatus: true,
    showCreator: true,
    showDate: true,
    showWorkspace: false,
    showProjects: false,
    showTasks: false,
    showDocuments: true,
    showReplyForm: false,
    showNestedReplies: false,
    spacing: 'sm',
    textSize: 'sm',
  }

  const config: NodeDetailConfig<Task> = {
    nodeType: 'task',
    service: taskServiceWithDocuments,
    tabs: [], // No tabs needed for task detail page
    getEditUrl: (id: string) => `/tasks/${id}/edit`,
    getListUrl: '/tasks',
    canEdit: (task: Task, user) => {
      // Allow editing if user is creator, assignee, or has admin role
      return (
        task.creator.id === user?.id || task.assignee?.id === user?.id || user?.role === 'admin'
      )
    },
    canDelete: (task: Task, user) => {
      // Allow deletion if user is creator or has admin role
      return task.creator.id === user?.id || user?.role === 'admin'
    },
    getMetadata: (task: Task) => (
      <>
        <NodePropertyBadge type="status" value={task.status} />
        {task.priority && <NodePropertyBadge type="priority" value={task.priority} />}
        {task.horizon && <NodePropertyBadge type="horizon" value={task.horizon} />}
        <NodePropertyBadge type="taskType" value={task.type} />
        {task.dueDate && <NodeDate dueDate={task.dueDate} className="text-sm text-foreground" />}
      </>
    ),
    getSidebarContent: (task: Task) => (
      <>
        {/* Associations */}
        <AssociationsCard
          className="lg:mt-6"
          associations={[
            { type: 'assignee' },
            { type: 'collaborators' },
            { type: 'workspace' },
            { type: 'projects' },
            { type: 'documents' },
          ]}
          data={{
            assignee: task.assignee,
            collaborators: task.users.filter((u) => u.id !== task.assignee?.id),
            workspace: task.workspace,
            projects: task.projects,
            documents: task.documents,
          }}
        />

        {/* History */}
        <HistoryCard creator={task.creator} createdAt={task.createdAt} updatedAt={task.updatedAt} />
      </>
    ),
    getBadges: () => ({}),
    getTabContent: () => null,
    loadingText: 'Loading task...',
    notFoundText: 'Task not found',
    backToListText: 'Back to Tasks',
    messageConfig,
  }

  return <NodeDetailPage config={config} />
}
